/**
 * Server-authored customer text, resolved per locale — FSD-18 D1 (the RCA
 * `state/lessons/hindi-journey-server-text-rendered-english.md`).
 *
 * The be sends the localised text of record on every issued shot (`promptTexts {en, hi}`, M6); `promptText` is only
 * the `en` value it keeps for older readers. Rendering `promptText` directly is how a Hindi journey showed English
 * instructions and how the Hindi voice came to read them. So this module is the ONE place a shot's words are read:
 * the instruction card, the challenge card, the review list, the receipt and the spoken cue all come through here
 * (`serverText.test.tsx` fails the build if a component reads `.promptText` / `.challenge.text` itself).
 *
 * The result names the locale the words are ACTUALLY in, which is not always the one asked for: a shot without a
 * Hindi rendering resolves to its English, and the guidance player uses that to refuse to read English in a Hindi
 * voice (D6).
 */

import type { ScriptShot } from '@/api/capture';

export interface ResolvedText {
  readonly text: string;
  /** The locale `text` is written in. `en` for the `promptText` fallback. */
  readonly locale: string;
}

function baseOf(locale: string): string {
  return (locale || 'en').toLowerCase().split('-')[0] ?? 'en';
}

function pick(texts: Readonly<Record<string, string>> | undefined, locale: string): ResolvedText | null {
  if (!texts) return null;
  const want = (locale || 'en').toLowerCase();
  const base = baseOf(want);
  for (const candidate of [want, base]) {
    for (const [key, value] of Object.entries(texts)) {
      if (key.toLowerCase() === candidate && typeof value === 'string' && value.trim() !== '') {
        return { text: value, locale: baseOf(key) };
      }
    }
  }
  return null;
}

/**
 * A shot's words in `locale`: `promptTexts[locale]` → `promptTexts[base]` → `promptTexts.en` → `promptText`.
 * On a challenge row this is ALSO the challenge sentence: the be issues `challenge.text === promptText` and the
 * bank entry's `{en, hi}` as `promptTexts` (`SvScriptService.issue()`, pinned by `serverText.test.tsx` against the
 * be's own wire dump), so the code card needs no second field.
 */
export function shotText(
  shot: { readonly promptText: string; readonly promptTexts?: ScriptShot['promptTexts'] | undefined },
  locale: string,
): ResolvedText {
  return pick(shot.promptTexts, locale) ?? pick(shot.promptTexts, 'en') ?? { text: shot.promptText, locale: 'en' };
}

/**
 * The challenge sentence in `locale`. `texts` are the issued shot's `promptTexts`. They are this sentence's
 * renderings ONLY while their `en` is the challenge's own text, which is what the be issues today. If a be ever
 * sends `challenge.text` that differs from the step's words, the challenge keeps its own (English) words. It never
 * borrows the step prompt, which the card would then hide as a repeat (FSD-20 review, 2026-10-23).
 */
export function challengeText(
  challenge: { readonly text: string },
  texts: ScriptShot['promptTexts'] | undefined,
  locale: string,
): ResolvedText {
  const own = challenge.text.trim();
  const sameSentence = texts !== undefined && (pick(texts, 'en')?.text.trim() ?? '') === own;
  return sameSentence ? shotText({ promptText: challenge.text, promptTexts: texts }, locale) : { text: challenge.text, locale: 'en' };
}

/** True when the resolved words are in the language the customer chose (base subtags compared). */
export function isInLocale(resolved: ResolvedText, locale: string): boolean {
  return baseOf(resolved.locale) === baseOf(locale);
}
